import useIntersectionObserver from '@react-hook/intersection-observer';
import { NextPage } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import Space from '@/components/Stream/Space';
import SearchForm from '@/components/Search/SearchForm';
import SearchTabs from '@/components/Search/SearchTabs';
import { IHashtagSections, audiences, getHashtag } from '@/lib/hashtags';
import { useAds } from '@/providers/AdProvider';
import { useSignupBanner } from '@/providers/SignupBannerProvider';

const POSTS_PER_PAGE = 6;

const ExplorePage: NextPage = () => {
  const { spaces, audience } = useAds();
  const { setMessage } = useSignupBanner();

  const [page, setPage] = useState(1);

  const ref = useRef(null);
  const { isIntersecting } = useIntersectionObserver(ref);

  useEffect(() => {
    document.body.classList.add('explore-body');
    setMessage('Join to explore more');

    return () => document.body.classList.remove('explore-body');
  }, [setMessage]);

  const paginate = useCallback(() => {
    setPage(pg => pg + 1);
  }, [setPage]);

  useEffect(() => {
    if (isIntersecting) {
      paginate();
    }
  }, [isIntersecting, paginate]);

  const getTopics = () => {
    if (audience && audiences && audiences[audience]) {
      return audiences[audience].map(h => {
        const ht = getHashtag(h as keyof IHashtagSections);
        if (ht) {
          return (
            <li key={`hashtag-${ht.slug}`} className="horizontal-scroll-item">
              <Link
                href={`/hashtag/${ht.slug}`}
                className="button is-rounded"
                style={{ backgroundColor: ht.colour }}>
                {ht.title}
              </Link>
            </li>
          );
        }
        return null;
      });
    }
    return null;
  };

  return (
    <div id="search" className="container">
      <Head>
        <title>Explore Shift.ms</title>
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/explore`}
        />
        <meta property="og:type" content="website" />
      </Head>
      <SearchForm />
      <div className="container is-narrow">
        <SearchTabs />
      </div>
      <div className="container">
        <div className="hashtags-trending">
          <h2 className="title is-size-3 is-size-4-mobile">Topics</h2>
          <ul className="horizontal-scroll">
            {getTopics()}
            <React.Fragment key="hashtag-browse-all">
              <li className="horizontal-scroll-item">
                <Link href="/hashtag" className="button is-rounded">
                  Browse all
                </Link>
              </li>
            </React.Fragment>
          </ul>
        </div>
        <div className="explore-items-container">
          {spaces.map((space, n) => (
            <React.Fragment key={`space-${space.id}`}>
              {n <= page * POSTS_PER_PAGE &&
              space.url !== 'https://shift.ms/explore' ? (
                <div className="explore-item">
                  <Space n={n} />
                </div>
              ) : null}
            </React.Fragment>
          ))}
        </div>
        <div className="obs" ref={ref} />
      </div>
    </div>
  );
};

export default ExplorePage;
